import { View, Text } from 'react-native';
import { warmupIntroSheetStyles as styles } from './WarmupIntroSheet.styles';
import { WARMUP_INTRO_TEST_IDS as IDS } from '../../../src/quiz/constants/warmupIntros.constants';
import { FOOTBALLER_COLUMNS } from '../../../src/quiz/constants/footballerQuiz.constants';

/**
 * Per-mode demo bodies for the how-to-play sheet. Each renders the area between the header and the
 * shared footer. Wordle + Footballer carry a colour legend; Higher or Lower shows a playable demo.
 */

type WordleSwatch = 'green' | 'amber' | 'grey';

const WORDLE_DEMO: { letter: string; swatch: WordleSwatch }[] = [
  { letter: 'S', swatch: 'green' },
  { letter: 'A', swatch: 'amber' },
  { letter: 'L', swatch: 'grey' },
  { letter: 'A', swatch: 'grey' },
  { letter: 'H', swatch: 'green' },
];

const WORDLE_LEGEND: { swatch: WordleSwatch; text: string }[] = [
  { swatch: 'green', text: 'Right letter, right spot' },
  { swatch: 'amber', text: 'In the name, wrong spot' },
  { swatch: 'grey', text: 'Not in the name' },
];

const FOOTBALLER_LEGEND: { swatch: WordleSwatch; text: string }[] = [
  { swatch: 'green', text: 'Exact match' },
  { swatch: 'amber', text: 'Nationality only: same continent' },
  { swatch: 'grey', text: 'No match' },
];

/** A demo Footballer guess vs a greener answer. Amber appears ONLY on the nationality tile (same
 *  continent, different country); every other tile is green-or-grey. The age tile shows a ▲/▼ to the answer. */
const FOOTBALLER_DEMO: { swatch: WordleSwatch; content: string; arrow?: string; flag?: boolean }[] = [
  { swatch: 'amber', content: '🇧🇷', flag: true }, // NAT: same continent (the only amber tile)
  { swatch: 'green', content: '🏴', flag: true }, // LGE: exact league
  { swatch: 'grey', content: '—' }, // TEAM: different club
  { swatch: 'green', content: 'FWD' }, // POS: exact
  { swatch: 'grey', content: '28', arrow: '▼' }, // AGE: not exact; ▼ = the answer is younger
];

function swatchStyle(swatch: WordleSwatch) {
  if (swatch === 'green') return styles.tileGreen;
  if (swatch === 'amber') return styles.tileAmber;
  return styles.tileGrey;
}

function Legend({ rows }: { rows: { swatch: WordleSwatch; text: string }[] }) {
  return (
    <View style={styles.legend}>
      {rows.map((row) => (
        <View key={row.text} style={styles.legendRow}>
          <View style={[styles.legendSwatch, swatchStyle(row.swatch)]} />
          <Text style={[styles.legendText, row.swatch === 'grey' && styles.legendTextDim]}>{row.text}</Text>
        </View>
      ))}
    </View>
  );
}

export function WordleIntroBody() {
  return (
    <View testID={IDS.wordleBody}>
      <View style={styles.tilesRow}>
        {WORDLE_DEMO.map((t, i) => (
          <View key={`${t.letter}-${i}`} style={[styles.tile, swatchStyle(t.swatch)]}>
            <Text
              style={[styles.tileLetter, t.swatch === 'grey' ? styles.tileLetterDim : styles.tileLetterOnBright]}
            >
              {t.letter}
            </Text>
          </View>
        ))}
      </View>
      <Legend rows={WORDLE_LEGEND} />
    </View>
  );
}

export function HiloIntroBody() {
  return (
    <View testID={IDS.hiloBody}>
      <View style={styles.hiloRows}>
        <View style={[styles.hiloRow, styles.hiloRowKnown]}>
          <View style={[styles.hiloAvatar, styles.hiloAvatarKnown]}>
            <Text style={styles.hiloAvatarText}>LM</Text>
          </View>
          <Text style={styles.hiloName}>Messi</Text>
          <View>
            <Text style={styles.hiloValue}>850</Text>
            <Text style={styles.hiloValueLabel}>GOALS</Text>
          </View>
        </View>
        <View style={[styles.hiloRow, styles.hiloRowMystery]}>
          <View style={[styles.hiloAvatar, styles.hiloAvatarMystery]}>
            <Text style={styles.hiloAvatarText}>KM</Text>
          </View>
          <Text style={styles.hiloName}>Mbappé</Text>
          <Text style={styles.hiloMysteryMark}>?</Text>
        </View>
      </View>
      <View style={styles.hiloChips}>
        <View style={[styles.hiloChip, styles.hiloChipMore]}>
          <Text style={[styles.hiloChipArrow, styles.hiloChipArrowMore]}>▲</Text>
          <Text style={[styles.hiloChipLabel, styles.hiloChipLabelMore]}>More</Text>
        </View>
        <View style={[styles.hiloChip, styles.hiloChipFewer]}>
          <Text style={[styles.hiloChipArrow, styles.hiloChipArrowFewer]}>▼</Text>
          <Text style={[styles.hiloChipLabel, styles.hiloChipLabelFewer]}>Fewer</Text>
        </View>
      </View>
    </View>
  );
}

function fqTextStyle(swatch: WordleSwatch) {
  if (swatch === 'green') return styles.fqTileTextOnBright;
  if (swatch === 'amber') return styles.fqTileTextAmber;
  return styles.fqTileTextDim;
}

export function FootballerIntroBody() {
  return (
    <View testID={IDS.footballerBody}>
      <View style={styles.fqLabels}>
        {FOOTBALLER_COLUMNS.map((col) => (
          <View key={col.key} style={styles.fqLabelCell}>
            <Text style={styles.fqLabelText}>{col.label}</Text>
          </View>
        ))}
      </View>
      <View style={styles.fqTilesRow}>
        {FOOTBALLER_DEMO.map((tile, i) => (
          <View
            key={i}
            style={[styles.fqTile, tile.swatch === 'grey' ? styles.fqTileEmpty : swatchStyle(tile.swatch)]}
          >
            {tile.flag ? (
              <Text style={styles.fqTileFlag}>{tile.content}</Text>
            ) : (
              <Text style={[styles.fqTileText, fqTextStyle(tile.swatch)]}>{tile.content}</Text>
            )}
            {tile.arrow ? <Text style={styles.fqTileArrow}>{tile.arrow}</Text> : null}
          </View>
        ))}
      </View>
      <Legend rows={FOOTBALLER_LEGEND} />
      <Text style={styles.fqArrowNote}>On AGE, ▲ / ▼ point toward the answer&apos;s age</Text>
    </View>
  );
}

/** Keeper Duel: three glyph rows — the loop, the clock, the hazard ladder. */
const KEEPER_DUEL_LEGEND: { glyph: string; text: string }[] = [
  { glyph: '🧤', text: 'The keeper never stops — flick into the space he leaves' },
  { glyph: '⏱️', text: 'Shoot before the clock runs out, or he steals it' },
  { glyph: '⚡', text: 'Every 3 goals a new hazard: defender, wind, rain…' },
];

export function KeeperDuelIntroBody() {
  return (
    <View testID={IDS.keeperDuelBody}>
      <View style={styles.legend}>
        {KEEPER_DUEL_LEGEND.map((row) => (
          <View key={row.glyph} style={styles.legendRow}>
            <Text style={styles.kdLegendGlyph}>{row.glyph}</Text>
            <Text style={[styles.legendText, styles.kdLegendText]}>{row.text}</Text>
          </View>
        ))}
      </View>
    </View>
  );
}
